import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { collection, doc, getDoc, onSnapshot, query, where } from "firebase/firestore";
import { db } from "../../firebase/config";

export type PendingStationApproval = {
  id: string;
  storeId: string;
  storeName: string;
  stationName: string;
};

type StationApprovalContextValue = {
  pending: PendingStationApproval[];
  bannerItems: PendingStationApproval[];
  dismissBanner: () => void;
};

const StationApprovalContext = createContext<StationApprovalContextValue>({
  pending: [],
  bannerItems: [],
  dismissBanner: () => undefined,
});

export function StationApprovalProvider({ children }: { children: React.ReactNode }) {
  const [pending, setPending] = useState<PendingStationApproval[]>([]);
  const [dismissed, setDismissed] = useState<string[]>([]);

  useEffect(() => {
    const requestQuery = query(
      collection(db, "stationRegistrationRequests"),
      where("status", "==", "PENDING")
    );
    let active = true;
    const unsubscribe = onSnapshot(requestQuery, async (snap) => {
      const rows = snap.docs.map((item) => {
        const data = item.data();
        return {
          id: item.id,
          storeId: String(data.restaurantId || ""),
          stationName: String(data.stationName || "Workstation"),
        };
      });
      const storeIds = Array.from(new Set(rows.map((item) => item.storeId).filter(Boolean)));
      const names: Record<string, string> = {};
      await Promise.all(storeIds.map(async (storeId) => {
        const storeSnap = await getDoc(doc(db, "stores", storeId));
        const name = storeSnap.exists() ? String(storeSnap.data().name || "").trim() : "";
        names[storeId] = name || "Store";
      }));
      if (!active) return;
      setPending(rows.map((item) => ({
        ...item,
        storeName: names[item.storeId] || "Store",
      })));
    });
    return () => {
      active = false;
      unsubscribe();
    };
  }, []);

  const dismissBanner = () => {
    setDismissed(pending.map((item) => item.id));
  };

  const bannerItems = pending.filter((item) => !dismissed.includes(item.id));

  const value = useMemo(() => ({
    pending,
    bannerItems,
    dismissBanner,
  }), [bannerItems, pending]);

  return (
    <StationApprovalContext.Provider value={value}>
      {children}
    </StationApprovalContext.Provider>
  );
}

export function useStationApprovals() {
  return useContext(StationApprovalContext);
}
